import React, { useState, useEffect } from 'react';
import CarbonCalculatorForm from './CarbonCalculatorForm';
import CarbonCalculatorResults from './CarbonCalculatorResults';
import CarbonCalculatorBreakdown from './CarbonCalculatorBreakdown';
import CarbonCalculatorCTA from './CarbonCalculatorCTA';

interface CalculatorState {
  projectDurationMonths: number;
  softwareType: string;
  expectedUsers: number;
  avgUsageHoursPerWeek: number;
  userRegion: string;
  devRegion: string;
  infraRegion: string;
  projectLifespanYears: number;
}

interface CalculatorResults {
  totalEnergy_kWh: number;
  totalCO2_kg: number;
  flightsParisToNY: number;
  breakdown: {
    devEnergy_kWh: number;
    devCO2_kg: number;
    infraEnergy_kWh: number;
    infraCO2_kg: number;
    userEnergy_kWh: number;
    userCO2_kg: number;
  };
}

const softwareTypes = [
  "Site vitrine",
  "Site e-commerce",
  "Application mobile",
  "Application web",
  "Application de bureau",
  "SaaS"
];

const regions = [
  { name: "France", factor: 0.05, countries: undefined },
  { name: "Scandinavie", factor: 0.05, countries: ["Suède", "Norvège", "Danemark", "Finlande"] },
  { name: "Europe de l'Ouest", factor: 0.20, countries: ["Allemagne", "Belgique", "Pays-Bas", "Luxembourg", "Suisse"] },
  { name: "Europe de l'Est", factor: 0.60, countries: ["Pologne", "Roumanie", "Hongrie", "Tchéquie", "Slovaquie"] },
  { name: "Amérique du Nord", factor: 0.40, countries: ["États-Unis", "Canada"] },
  { name: "Amérique du Sud", factor: 0.25, countries: ["Brésil", "Argentine", "Chili", "Colombie"] },
  { name: "Amérique Centrale", factor: 0.30, countries: ["Mexique", "Costa Rica", "Panama"] },
  { name: "Afrique du Nord", factor: 0.45, countries: ["Maroc", "Tunisie", "Algérie", "Égypte"] },
  { name: "Afrique Sub-Saharienne", factor: 0.55, countries: ["Sénégal", "Côte d'Ivoire", "Kenya", "Afrique du Sud"] },
  { name: "Asie du Nord", factor: 0.65, countries: ["Chine", "Japon", "Corée du Sud"] },
  { name: "Asie du Sud", factor: 0.75, countries: ["Inde", "Pakistan", "Bangladesh"] },
  { name: "Asie du Sud-Est", factor: 0.60, countries: ["Thaïlande", "Vietnam", "Indonésie", "Malaisie"] },
  { name: "Océanie", factor: 0.50, countries: ["Australie", "Nouvelle-Zélande"] },
  { name: "Moyen-Orient", factor: 0.55, countries: ["Émirats Arabes Unis", "Arabie Saoudite", "Israël"] },
  { name: "Moyenne Mondiale", factor: 0.45, countries: undefined }
];

const complexityFactors = {
  "Site vitrine": 0.5,
  "Site e-commerce": 0.8,
  "Application mobile": 1.0,
  "Application web": 1.2,
  "Application de bureau": 1.5,
  "SaaS": 1.5
};

const infraEnergyPerUserPerYear_kWh = {
  "Site vitrine": 0.2,
  "Site e-commerce": 1,
  "Application mobile": 2.5,
  "Application web": 2,
  "Application de bureau": 12,
  "SaaS": 5
};

const devicePower_W = {
  "Site vitrine": 10,
  "Site e-commerce": 20,
  "Application mobile": 3,
  "Application web": 20,
  "Application de bureau": 60,
  "SaaS": 40
};

const CarbonCalculator = () => {
  const [state, setState] = useState<CalculatorState>({
    projectDurationMonths: 0,
    softwareType: "",
    expectedUsers: 0,
    avgUsageHoursPerWeek: 0,
    userRegion: "",
    devRegion: "",
    infraRegion: "",
    projectLifespanYears: 0
  });

  const [results, setResults] = useState<CalculatorResults>({
    totalEnergy_kWh: 0,
    totalCO2_kg: 0,
    flightsParisToNY: 0,
    breakdown: {
      devEnergy_kWh: 0,
      devCO2_kg: 0,
      infraEnergy_kWh: 0,
      infraCO2_kg: 0,
      userEnergy_kWh: 0,
      userCO2_kg: 0
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  const validateForm = () => {
    const errors: Record<string, boolean> = {};
    
    if (state.projectDurationMonths <= 0) errors.projectDurationMonths = true;
    if (!state.softwareType) errors.softwareType = true;
    if (state.expectedUsers <= 0) errors.expectedUsers = true;
    if (state.avgUsageHoursPerWeek <= 0) errors.avgUsageHoursPerWeek = true;
    if (!state.userRegion) errors.userRegion = true;
    if (!state.devRegion) errors.devRegion = true;
    if (!state.infraRegion) errors.infraRegion = true;
    if (state.projectLifespanYears <= 0) errors.projectLifespanYears = true;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCalculate = () => {
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setHasCalculated(true);
    
    // Calculate results immediately
    calculateResults();
    setIsLoading(false);
  };

  const calculateResults = () => {
    // Developer Count
    const devCount = Math.max(1, Math.round(complexityFactors[state.softwareType] * Math.sqrt(state.projectDurationMonths)));
    
    // Developer Energy (Selected region)
    const devEnergy_kWh = devCount * state.projectDurationMonths * 21 * 8 * 0.1 * 1.1/5;
    const devRegionFactor = regions.find(r => r.name === state.devRegion)?.factor || 0.05;
    const devCO2_kg = devEnergy_kWh * devRegionFactor;
    
    // Infrastructure Energy (Selected region) - Now multiplied by lifespan
    const infraEnergy_kWh = state.expectedUsers * infraEnergyPerUserPerYear_kWh[state.softwareType] * state.projectLifespanYears * 1.4;
    const infraRegionFactor = regions.find(r => r.name === state.infraRegion)?.factor || 0.05;
    const infraCO2_kg = infraEnergy_kWh * infraRegionFactor;
    
    // User Device Energy (User's region) - Now multiplied by lifespan
    const userDeviceEnergy_kWh = state.expectedUsers * state.avgUsageHoursPerWeek * 52 * state.projectLifespanYears * (devicePower_W[state.softwareType] / 1000);
    const userRegionFactor = regions.find(r => r.name === state.userRegion)?.factor || 0.45;
    const userDeviceCO2_kg = userDeviceEnergy_kWh * userRegionFactor;
    
    // Total Energy and CO2
    const totalEnergy_kWh = devEnergy_kWh + infraEnergy_kWh + userDeviceEnergy_kWh;
    const totalCO2_kg = devCO2_kg + infraCO2_kg + userDeviceCO2_kg;
    
    // Additional Metrics
    const flightsParisToNY = totalCO2_kg / 1600;

    setResults({
      totalEnergy_kWh,
      totalCO2_kg,
      flightsParisToNY,
      breakdown: {
        devEnergy_kWh,
        devCO2_kg,
        infraEnergy_kWh,
        infraCO2_kg,
        userEnergy_kWh: userDeviceEnergy_kWh,
        userCO2_kg: userDeviceCO2_kg
      }
    });
  };

  const handleInputChange = (field: keyof CalculatorState, value: string | number) => {
    setState(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error when user starts typing
    setValidationErrors(prev => ({
      ...prev,
      [field]: false
    }));
  };

  return (
    <section className="pt-24 bg-gradient-to-b from-slate-50 to-white" id="carbon-calculator">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Calculez l'empreinte carbone de votre projet
            </h2>
            <p className="text-xl text-slate-600">
              Estimez l'impact environnemental de votre projet digital et découvrez comment nous pouvons le compenser.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-1">
              <CarbonCalculatorForm 
                state={state} 
                softwareTypes={softwareTypes} 
                regions={regions} 
                onInputChange={handleInputChange}
                validationErrors={validationErrors}
              />
              {Object.keys(validationErrors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">
                    Veuillez remplir tous les champs obligatoires pour calculer l'empreinte carbone.
                  </p>
                </div>
              )}
              <button
                onClick={handleCalculate}
                disabled={isLoading}
                className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Calcul en cours...</span>
                  </div>
                ) : (
                  'Calculer'
                )}
              </button>
            </div>

            <div className="lg:col-span-2 space-y-6">
              {!hasCalculated ? (
                <div className="hidden lg:flex items-center justify-center min-h-[600px] bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-slate-500 text-xl">Vos résultats apparaîtront ici</p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center min-h-[600px] bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-600">Calcul en cours...</p>
                  </div>
                </div>
              ) : (
                <>
                  <CarbonCalculatorResults results={results} />
                  <CarbonCalculatorBreakdown results={results} state={state} />
                </>
              )}
            </div>
          </div>

          {hasCalculated && !isLoading && (
            <CarbonCalculatorCTA state={state} results={results} />
          )}
        </div>
      </div>
    </section>
  );
};

export default CarbonCalculator; 